import { z } from 'zod';
import { AlignmentChoiceSchema } from './alignment';
import { BondSchema } from './bond';
import { DamageDieSchema } from './classDefinition';
import { ClassIdSchema } from './classId';
import { ItemSchema } from './equipment';
import { FollowerSchema } from './follower';
import { LevelUpRecordSchema } from './levelUpRecord';
import { RaceChoiceSchema } from './race';
import { StatsSchema } from './stats';

/**
 * Versione corrente dello schema Character.
 *
 * Bumpare quando si introducono cambi incompatibili e predisporre la migration
 * in `services/characterMigration.ts`.
 *
 * **Storia**:
 *  - **v1**: bootstrap (PR foundation). Solo i campi base; archetipo non
 *    rappresentato sulla scheda PG; `damage` come stringa libera.
 *  - **v2** (questa): foundation chiusa. Aggiunti `archetypeId` (obbligatorio),
 *    `chosenAspetti`, `title` opzionale; `damage` ristretto a `DamageDieSchema`.
 *    Allineamento alla procedura di creazione PG del manuale V1.
 */
export const CHARACTER_SCHEMA_VERSION = 2 as const;

/**
 * Esito del tiro al reclutamento di una mossa-source di Follower con
 * `FollowerTemplate.recruitRoll` definito (D8a — Supporto Elementale Druido,
 * Animare Zombi Sciamano). Modella il riassunto del tiro fisico fatto al
 * tavolo: 10+ pieno successo, 7-9 successo parziale. Il fallimento (6-)
 * non concede il follower e non viene salvato qui.
 */
export const RecruitOutcomeSchema = z.enum(['10+', '7-9']);
export type RecruitOutcome = z.infer<typeof RecruitOutcomeSchema>;

/**
 * Riferimento a una mossa presa dal PG.
 * Solo l'ID stabile (la mossa "vera" sta nel catalogo `src/data/moves`),
 * più eventuali scelte fatte dal giocatore per mosse con sotto-struttura
 * (es. *Animale Totemico*: scelta dell'animale).
 *
 * **Pattern setup mossa (FI-18)**:
 * - **`selectedChoices`**: shorthand single-group, lista piatta di ID delle
 *   opzioni scelte (per mosse con `MoveSchema.choices`). Default `[]`.
 * - **`selectedChoiceGroups`**: multi-gruppo, mappa `groupId → IDs scelti`
 *   (per mosse con `MoveSchema.choiceGroups`). Esempio per *Arma Prescelta*:
 *   `{ tipologia: ['spada'], portata: ['media'], miglioramenti: ['affilata',
 *   'perfettamente-bilanciata'] }`. Opzionale.
 * - **`narrativeFieldValues`**: valori dei campi narrativi liberi compilati al
 *   setup della mossa, mappa `fieldId → testo`. Esempio per *Arma Prescelta*:
 *   `{ 'descrizione-arma': 'La mia spada di famiglia', 'nome-arma':
 *   'Vendichiana' }`. Opzionale.
 * - **`recruitOutcome`** (D8a): esito del tiro al reclutamento per
 *   mosse-source di Follower con `FollowerTemplate.recruitRoll` (Druido
 *   Supporto Elementale, Sciamano Animare Zombi). Driver del `pickCount`
 *   effettivo del gruppo di peculiarità. Opzionale: presente solo sulle
 *   mosse con recruit roll.
 *
 * **Backward-compat**: i campi nuovi sono opzionali. Draft v2 esistenti
 * senza i campi parsano correttamente. Niente bump di
 * `CHARACTER_SCHEMA_VERSION`.
 */
export const MoveRefSchema = z.object({
  /** ID della mossa nel catalogo. */
  id: z.string().min(1),
  /** ID delle scelte selezionate (per mosse con `choices` shorthand). */
  selectedChoices: z.array(z.string()).default([]),
  /**
   * Mappa `groupId → IDs scelti` per mosse con `choiceGroups` multi-gruppo.
   * (FI-18). Opzionale: assente per mosse `choices` shorthand o senza scelte.
   */
  selectedChoiceGroups: z.record(z.string(), z.array(z.string())).optional(),
  /**
   * Mappa `fieldId → valore testuale` per mosse con `narrativeFields`.
   * (FI-18). Opzionale: assente per mosse senza setup narrativo.
   * La validazione "non-vuoto" è applicata al predicate del builder, non qui.
   */
  narrativeFieldValues: z.record(z.string(), z.string()).optional(),
  /**
   * Esito del tiro al reclutamento (D8a). Presente solo per mosse-source di
   * Follower con `FollowerTemplate.recruitRoll`. Driver del pickCount runtime.
   */
  recruitOutcome: RecruitOutcomeSchema.optional(),
});
export type MoveRef = z.infer<typeof MoveRefSchema>;

/**
 * Riferimento a un incantesimo conosciuto/preparato dal PG.
 * Per ora solo l'ID; il dettaglio sta nel catalogo `src/data/spells`.
 */
export const SpellRefSchema = z.object({
  id: z.string().min(1),
});
export type SpellRef = z.infer<typeof SpellRefSchema>;

/**
 * Scelta del giocatore per una categoria di Aspetto.
 *
 * Le categorie disponibili (Occhi, Corpo, Vestiario, …) e le opzioni
 * proposte sono definite dalla `ClassDefinition.aspetti` della classe del PG.
 * Sulla scheda PG memorizziamo la scelta finale: la categoria a cui appartiene
 * + il valore selezionato (uno degli `options` della categoria, oppure un
 * valore custom se la UI lo permetterà).
 *
 * Esempio Barbaro: `{ categoria: 'Occhi', valore: 'Spiritati' }`.
 */
export const ChosenAspettoSchema = z.object({
  /** Etichetta della categoria, deve combaciare con `AspettoCategoria.label`. */
  categoria: z.string().min(1).max(50),
  /** Valore scelto dal giocatore (uno degli `options` della categoria). */
  valore: z.string().min(1).max(150),
});
export type ChosenAspetto = z.infer<typeof ChosenAspettoSchema>;

/**
 * Schema principale del personaggio (scheda PG completa).
 *
 * Le scelte fatte in fase di creazione/level-up (classe, razza, archetipo,
 * mosse, incantesimi, equipaggiamento, legami, aspetto) sono qui aggregate.
 *
 * Le sezioni opzionali per la modalità "giocabile" (HP/PE/condizioni live,
 * gregari attivi, debilitazioni in corso) verranno aggiunte in PR successive.
 *
 * **Invariante non enforced dallo schema** (validati a livello service):
 *  - almeno 1 legame compilato dopo la creazione (FI-4)
 *  - se uno spell ha `requiresArchetype`, il PG deve avere quell'archetipo (FI-7)
 *  - vincoli cross-field tra `classId`, `archetypeId` (l'archetipo deve essere
 *    uno di quelli definiti in `ClassDefinition.archetypes`)
 */
export const CharacterSchema = z.object({
  schemaVersion: z.literal(CHARACTER_SCHEMA_VERSION),
  id: z.uuid(),
  name: z.string().min(1).max(50),
  /**
   * Titolo/epiteto opzionale del PG (manuale V1: lista *Titoli* presente nel
   * libretto del Barbaro; il giocatore può inventarne uno custom anche per
   * altre classi).
   */
  title: z.string().max(100).optional(),
  classId: ClassIdSchema,
  /**
   * ID dell'archetipo scelto dal PG. Deve combaciare con uno degli
   * `ClassDefinition.archetypes[].id` per la classe di questo PG.
   * Obbligatorio: il manuale richiede la scelta in fase di creazione (passo 5).
   */
  archetypeId: z.string().min(1),
  /**
   * Secondo archetipo concesso da *Infanzia Complessa* (mossa comune
   * disponibile esattamente al 2° livello — manuale V1 r. 1787). Deve
   * combaciare con uno degli `ClassDefinition.archetypes[].id` per la
   * classe del PG, **diverso** da `archetypeId`.
   *
   * **Effetti cross-field** (validati a livello service, non schema):
   *  - aggiunge la mossa archetipo del 2° archetipo a `moves` (via
   *    `startingChoiceGroups` con `requiresArchetype`)
   *  - estende l'accesso a spell con `requiresArchetype:
   *    secondaryArchetypeId` (es. Chierico-Nano sblocca *Voce della Terra*)
   *  - applica `baseHpOverride` se presente sul 2° archetipo (FI-1 esteso:
   *    proprietà della combo classe+archetipo, non legata alla creazione).
   *    Caso noto: Paladino-Umano L1 → IC Nano L2 → HP ricalcolato
   *    `12 + COS` (delta +2 a `hp.max` e `hp.current`).
   *
   * **Additivo, non breaking**: campo opzionale, niente bump
   * `CHARACTER_SCHEMA_VERSION`. Vedi
   * `dev-workspace/docs/Fase-D-Level-Up-SSOT.md` §2.1.
   */
  secondaryArchetypeId: z.string().min(1).optional(),
  /**
   * Livello del PG (1+). Cap a 10 rimosso in D10 (FI-11): il livello può
   * eccedere 10, ma le formule level-derived (`Mana` count, hp scaling
   * gregari, ecc.) restano cappate a 10 via `getCappedLevel(character)`.
   * Sopra il 10° il giocatore sceglie {@link pastTen} (Addio / Apprendista /
   * Instancabile).
   */
  level: z.number().int().min(1),
  /**
   * **D10 — FI-11**: scelta cruciale del giocatore quando il PG supera il
   * 10° livello (manuale V1 rr. 1824-1834). Tre opzioni esclusive:
   *  - `'addio'`: il PG si ritira dall'avventura. Il livello non incrementa
   *    più; il reducer blocca nuovi level-up.
   *  - `'apprendista'`: il giocatore controlla anche un secondo PG legato
   *    (gestione multi-PG → Fase F). Il senior continua a crescere come se
   *    avesse scelto Instancabile.
   *  - `'instancabile'`: il PG continua. Le formule level-derived sono
   *    cappate a 10 via `getCappedLevel(character)`; le Caratteristiche
   *    non aumentano più (`applyLevelUp` salta `statIncreased`); 5 mosse
   *    comuni multiclasse a catena diventano accessibili.
   *
   * Presente solo quando `level > 10`. Vincolo applicativo (non enforced
   * dallo schema): se `level > 10`, `pastTen` deve essere definito.
   */
  pastTen: z.enum(['addio', 'apprendista', 'instancabile']).optional(),
  xp: z.number().int().min(0).default(0),
  race: RaceChoiceSchema,
  alignment: AlignmentChoiceSchema,
  stats: StatsSchema,
  /**
   * Scelte di Aspetto del giocatore (Occhi, Corpo, Vestiario, …).
   * Tipicamente 3-4 entry, una per categoria definita dalla classe.
   * Default vuoto per consentire creazione PG incrementale.
   */
  chosenAspetti: z.array(ChosenAspettoSchema).default([]),
  hp: z.object({
    current: z.number().int().min(0),
    max: z.number().int().min(1),
  }),
  armor: z.number().int().min(0).default(0),
  /**
   * Dado di danno base del PG, copia di `ClassDefinition.damageDie` al momento
   * della creazione. Memorizzato (non derivato) per autosufficienza della
   * scheda nei modi "stampabile" e "import/export" (la scheda dev'essere
   * leggibile anche senza catalogo classi disponibile).
   */
  damage: DamageDieSchema,
  load: z.object({
    current: z.number().min(0),
    max: z.number().min(0),
  }),
  moves: z.array(MoveRefSchema).default([]),
  spells: z.array(SpellRefSchema).default([]),
  inventory: z.array(ItemSchema).default([]),
  bonds: z.array(BondSchema).default([]),
  /**
   * Se `true`, il giocatore ha posticipato la compilazione dei legami alla
   * prima sessione di gioco al tavolo (FI-25). I `bonds` possono essere vuoti
   * senza che ciò blocchi la finalizzazione del PG. Default `undefined` =
   * legami compilati normalmente alla creazione.
   *
   * **Why**: i legami sono con altri PG; al momento della creazione del PG
   * potresti non sapere ancora chi siano gli altri compagni. Il GM può
   * ricordare al giocatore di compilarli all'inizio della prima sessione.
   *
   * **Additivo, non breaking**: il campo è opzionale, niente bump
   * `CHARACTER_SCHEMA_VERSION`.
   */
  bondsDeferred: z.boolean().optional(),
  /**
   * Seguaci del PG (gregari, evocazioni, compagni animali). Manuale V1
   * rr. 1267-1426. Vedi `FollowerSchema` per il dettaglio dei campi.
   *
   * **Additivo, non breaking**: default `[]`, niente bump
   * `CHARACTER_SCHEMA_VERSION`. La maggior parte dei PG ha 0 follower;
   * quelli con mosse-source produttive (Ranger con *Compagno Animale*, Mago
   * con *Famiglio*, …) ne hanno 1+ a seconda della classe/level-up.
   *
   * **Vincoli applicativi** validati a livello service:
   *  - i `followers[i].sourceMoveId` corrispondono a mosse effettivamente
   *    in `moves`
   *  - limiti "1 alla volta" (Supporto Elementale, Animare Zombi, …)
   *  - eccezioni come *Un Giusto Sovrano* del Warlock (2 Bracci Destri,
   *    1 attivo per giorno)
   */
  followers: z.array(FollowerSchema).default([]),
  /**
   * Audit log delle transizioni di livello del PG. Ogni `LevelUpRecord`
   * registra le scelte fatte a un singolo level-up: stat aumentata,
   * mossa scelta, eventuali mosse soppiantate/bundle, spell aggiunto,
   * secondo archetipo (Infanzia Complessa).
   *
   * **Why**: ricostruzione deterministica della build, debugging,
   * futura UI «cronologia degli avanzamenti» (DA-15 della SSOT Fase D).
   *
   * **Invariante** (validato a livello service): la `length` di
   * `levelUpHistory` deve essere `level - 1` (un record per ogni
   * livello sopra il 1°). PG creati al L1 hanno `[]`.
   *
   * **Additivo, non breaking**: default `[]`, niente bump
   * `CHARACTER_SCHEMA_VERSION`. PG salvati prima di D1 sono parsati
   * con `levelUpHistory: []` (coerente con `level === 1`).
   */
  levelUpHistory: z.array(LevelUpRecordSchema).default([]),
  notes: z.string().default(''),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type Character = z.infer<typeof CharacterSchema>;
